import { useState } from "react";
import useSWR from "swr";
import { Minus, TrendingDown, TrendingUp, Trophy } from "lucide-react";
import { fetcher } from "@/lib/api";

const Variation = ({ value }) => {
  if (!value) {
    return (
      <span className="text-[11px] text-muted-foreground inline-flex items-center gap-0.5">
        <Minus className="h-3 w-3" />igual
      </span>
    );
  }
  const up = value > 0;
  return (
    <span className={`text-[11px] inline-flex items-center gap-0.5 ${
      up ? "text-primary" : "text-destructive"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? "+" : ""}{value}
    </span>
  );
};

const Row = ({ item, max, position, goal }) => {
  const width = max ? Math.max(3, Math.round((item.publicadas / max) * 100)) : 0;
  const meta = goal ? Math.min(100, item.cumplimiento || 0) : 0;
  return (
    <li className="py-3 border-b border-border/60 last:border-0" data-testid={`rank-row-${item.key}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="mono text-xs text-muted-foreground w-4 shrink-0">{position}</span>
          <span className="text-sm font-medium truncate">{item.name}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="mono text-sm font-semibold">{item.publicadas}</span>
          <Variation value={item.variacion} />
        </div>
      </div>
      <div className="h-1.5 bg-muted mt-2 overflow-hidden">
        <div className="h-full bg-primary transition-[width] duration-500"
             style={{ width: `${width}%` }} />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px] text-muted-foreground">
        <span>{item.en_revision} en revisión</span>
        <span>SEO {item.seo || "—"}</span>
        {goal && (
          <span className={meta >= 100 ? "text-primary" : ""}>
            Meta {item.publicadas}/{item.meta} · {item.cumplimiento}%
          </span>
        )}
      </div>
    </li>
  );
};

const List = ({ title, items, goal, testid }) => {
  const max = Math.max(1, ...items.map((i) => i.publicadas));
  return (
    <div className="border border-border bg-card p-6" data-testid={testid}>
      <div className="kicker mb-1 flex items-center gap-2">
        <Trophy className="h-3.5 w-3.5 text-primary" />{title}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-3">Sin producción en la semana.</p>
      ) : (
        <ul className="mt-2">
          {items.slice(0, 8).map((item, n) => (
            <Row key={item.key} item={item} max={max} position={n + 1} goal={goal} />
          ))}
        </ul>
      )}
    </div>
  );
};

export const WeeklyRanking = () => {
  const [week, setWeek] = useState(0);
  const { data } = useSWR(`/stats/weekly-ranking?weeks_back=${week}`, fetcher,
    { refreshInterval: 120000 });

  return (
    <div className="mt-8" data-testid="weekly-ranking">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="kicker text-primary">Ranking semanal</div>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.range ? `Semana del ${data.range}` : "Cargando…"} ·{" "}
            {data?.totals?.publicadas ?? 0} publicadas · compara con {data?.prev_range || "—"}
          </p>
        </div>
        <div className="flex border border-border">
          {[["Esta semana", 0], ["Semana pasada", 1]].map(([label, value]) => (
            <button
              key={value}
              onClick={() => setWeek(value)}
              className={`text-xs px-3 py-2 transition-colors ${
                week === value ? "bg-primary text-primary-foreground"
                               : "text-muted-foreground hover:bg-muted/60"}`}
              data-testid={`rank-week-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <List title="Medios" items={data?.outlets || []} testid="rank-outlets" />
        <List title="Redactores" items={data?.authors || []} goal testid="rank-authors" />
      </div>
    </div>
  );
};
